import type { SxProps, Theme } from '@mui/material';

// Pinned to the top of the page scroll area so the prompt stays reachable from anywhere in a tall
// dashboard. `top: 0` is relative to PageContent, which owns the scroll for /intelligence/*.
//
// The opaque background is what stops tiles showing through as they scroll under it. `paper` (white)
// rather than `default` (neutral[50]): the layout leaves the page white, so `default` would read as
// a grey band. No margin cancelling is needed — the bar and the grid are siblings inside the same
// padded root, so a tile can never be wider than this background.
export const promptBarWrapperSx: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  paddingBottom: 2,
  paddingTop: 1,
  position: 'sticky',
  top: 0,
  // Above the grid cards, below the app's navigation and any popover.
  zIndex: 2,
};

// Collapsed: one line, the whole row is the click target.
export const collapsedBarSx: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: 'background.paper',
  border: theme => `1px solid ${theme.palette.neutral[200]}`,
  borderRadius: theme => `${theme.shape.radius.l}px`,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  gap: 2,
  padding: '12px 20px',
  textAlign: 'left',
  transition: theme => theme.transitions.create(['border-color', 'background-color'], { duration: 160 }),
  width: '100%',
  '&:hover': {
    backgroundColor: theme => theme.palette.neutral[50],
    borderColor: theme => theme.palette.neutral[300],
  },
  '&:focus-visible': {
    borderColor: theme => theme.palette.brand[400],
    outline: 'none',
  },
};

export const collapsedIconSx: SxProps<Theme> = {
  flexShrink: 0,
  height: 22,
  width: 22,
};

// One line with an ellipsis: the collapsed bar must not change height with prompt length, or the
// grid below it would shift every time a dashboard is regenerated.
const collapsedTextBase = {
  flex: 1,
  fontSize: 15,
  lineHeight: '22px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const collapsedTextSx: SxProps<Theme> = {
  ...collapsedTextBase,
  color: 'text.primary',
};

export const collapsedPlaceholderTextSx: SxProps<Theme> = {
  ...collapsedTextBase,
  color: theme => theme.palette.neutral[400],
};

export const collapsedEditLabelSx: SxProps<Theme> = {
  color: theme => theme.palette.brand[600],
  flexShrink: 0,
  fontSize: 13,
  fontWeight: 600,
};

export const promptBarErrorSx: SxProps<Theme> = {
  marginTop: 2,
};
